/**
 * Helper functions for CreateMeetingPopup component
 */
import moment from 'moment'

import { formatLocalizedDate, momentLocaleFor } from 'i18n/formatDate'

/**
 * Calendar day of a picked Date, read from its own local components.
 *
 * The date pickers build values with `new Date(y, m, d)` (local midnight), so the
 * only reliable way to recover the day the user clicked is to read
 * getFullYear/getMonth/getDate. Re-reading the instant through moment resolves it
 * in moment's ambient zone, which shifts the calendar day whenever that zone sits
 * behind the browser's.
 */
export const WALL_CLOCK_FORMAT = 'YYYY-MM-DD HH:mm'

export const toWallClockDay = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Locale-aware short time (en: "1:30 PM", fr: "13:30"). */
export const localizedTime = (hours: number, minutes: number, lang?: string | null): string =>
  moment({ hour: hours, minute: minutes }).locale(momentLocaleFor(lang)).format('LT')

/**
 * Formats time as HH:MM with zero-padding
 */
export const formatTimeString = (hours: number, minutes: number): string => {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Rounds minutes to next 15-minute interval
 * Returns both rounded minutes and whether to add an hour
 */
export const roundToNext15Minutes = (
  minutes: number
): { roundedMinutes: number; addHour: boolean } => {
  const roundedMinutes = Math.ceil(minutes / 15) * 15
  return {
    roundedMinutes: roundedMinutes >= 60 ? 0 : roundedMinutes,
    addHour: roundedMinutes >= 60,
  }
}

/**
 * Gets current time in specified timezone with 15-minute rounding
 */
export const getCurrentTimeInTimezone = (timezone: string): { hour: number; minute: number } => {
  const now = new Date()

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    const parts = formatter.formatToParts(now)
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0')
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0')

    const { roundedMinutes, addHour } = roundToNext15Minutes(minute)

    return {
      hour: addHour ? hour + 1 : hour,
      minute: roundedMinutes,
    }
  } catch (error) {
    // Fallback to local time
    const { roundedMinutes, addHour } = roundToNext15Minutes(now.getMinutes())

    return {
      hour: addHour ? now.getHours() + 1 : now.getHours(),
      minute: roundedMinutes,
    }
  }
}

/**
 * Adds minutes to a time string and returns new time
 */
export const addMinutesToTime = (timeString: string, minutesToAdd: number): string => {
  const [hours, minutes] = timeString.split(':').map(Number)
  const endDate = new Date(2000, 0, 1, hours, minutes)
  endDate.setMinutes(endDate.getMinutes() + minutesToAdd)
  return formatTimeString(endDate.getHours(), endDate.getMinutes())
}

/**
 * Length in minutes between two "HH:mm" times. An end that is not after the
 * start is treated as crossing midnight, matching how the create flow already
 * auto-sets 12:30 AM for an 11:30 PM start.
 */
export const minutesBetweenTimes = (startTime: string, endTime: string): number => {
  const [startHours, startMinutes] = startTime.split(':').map(Number)
  const [endHours, endMinutes] = endTime.split(':').map(Number)
  const diff = endHours * 60 + endMinutes - (startHours * 60 + startMinutes)
  return diff > 0 ? diff : diff + 24 * 60
}

/**
 * Formats minute with leading zero
 */
export const formatMinute = (minute: number): string => {
  return minute.toString().padStart(2, '0')
}

/**
 * Gets timezone display string with offset
 */
export const getTimezoneDisplay = (timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
    const parts = formatter.formatToParts(new Date())
    const offsetPart = parts.find((part) => part.type === 'timeZoneName')
    const formattedOffset = offsetPart ? offsetPart.value : 'GMT+00:00'

    return `(${formattedOffset}) ${timezone.replace('_', ' ')}`
  } catch (error) {
    return '(GMT+00:00) UTC'
  }
}

/**
 * Filters time slots to show only future times for today in local timezone
 */
export const getValidStartTimes = (
  selectedDate: Date | null,
  selectedTimezone: string,
  deviceTimezone: string,
  lang?: string | null
): { value: string; display: string }[] => {
  const allSlots = generateTimeSlots(lang)

  if (!selectedDate) return allSlots

  const today = new Date()
  const isToday = selectedDate.toDateString() === today.toDateString()

  if (!isToday) return allSlots
  if (selectedTimezone !== deviceTimezone) return allSlots

  const now = new Date()
  const currentHours = now.getHours()
  const currentMinutes = now.getMinutes()
  const currentTimeInMinutes = currentHours * 60 + currentMinutes

  return allSlots.filter((slot) => {
    const [hours, minutes] = slot.value.split(':').map(Number)
    const slotTimeInMinutes = hours * 60 + minutes
    return slotTimeInMinutes > currentTimeInMinutes
  })
}

/**
 * Filters time slots to show only times after start time
 */
export const getValidEndTimes = (
  startTime: string,
  lang?: string | null
): { value: string; display: string }[] => {
  const allSlots = generateTimeSlots(lang)
  if (!startTime) return allSlots

  return allSlots.filter((slot) => slot.value > startTime)
}

/**
 * Formats date for display
 */
export const formatDate = (date: Date, lang?: string | null): string =>
  formatLocalizedDate(date, lang, 'ddd ll')

/**
 * Formats time for display (12-hour format)
 */
export const formatTimeDisplay = (timeString: string, lang?: string | null): string => {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':').map(Number)
  return localizedTime(hours, minutes, lang)
}

/**
 * Validates email address format
 * - Maximum length of 254 characters (RFC 5321)
 * - Prevents multiple consecutive dots
 * - Validates proper domain structure with TLD
 * - Restricts invalid special characters
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false
  }

  // Check maximum email length (RFC 5321 specifies 254 characters)
  if (email.length > 254) {
    return false
  }

  // Check for multiple consecutive dots anywhere in the email
  if (email.includes('..')) {
    return false
  }

  // Check for invalid special characters (only allow: alphanumeric, dots, hyphens, underscores, plus signs, @)
  const invalidCharRegex = /[^a-zA-Z0-9._+\-@]/
  if (invalidCharRegex.test(email)) {
    return false
  }

  // Split email into local and domain parts
  const parts = email.split('@')
  if (parts.length !== 2) {
    return false
  }

  const [localPart, domainPart] = parts

  // Validate local part (before @)
  // - Cannot be empty
  // - Cannot start or end with a dot
  // - Cannot have consecutive dots (already checked above)
  // - Can contain: alphanumeric, dots, hyphens, underscores, plus signs
  if (
    !localPart ||
    localPart.length === 0 ||
    localPart.startsWith('.') ||
    localPart.endsWith('.') ||
    localPart.startsWith('-') ||
    localPart.endsWith('-')
  ) {
    return false
  }

  // Validate domain part (after @)
  // - Cannot be empty
  // - Must have at least one dot (for TLD)
  // - Cannot start or end with a dot or hyphen
  // - Must have valid TLD (at least 2 characters after last dot)
  if (
    !domainPart ||
    domainPart.length === 0 ||
    !domainPart.includes('.') ||
    domainPart.startsWith('.') ||
    domainPart.endsWith('.') ||
    domainPart.startsWith('-') ||
    domainPart.endsWith('-')
  ) {
    return false
  }

  // Validate TLD (top-level domain)
  const domainParts = domainPart.split('.')
  const tld = domainParts[domainParts.length - 1]
  if (!tld || tld.length < 2 || /[^a-zA-Z0-9]/.test(tld)) {
    return false
  }

  // Validate each domain segment (between dots)
  for (const segment of domainParts) {
    if (!segment || segment.length === 0) {
      return false
    }
    // Domain segments can contain alphanumeric and hyphens, but not start/end with hyphen
    if (segment.startsWith('-') || segment.endsWith('-')) {
      return false
    }
    if (!/^[a-zA-Z0-9-]+$/.test(segment)) {
      return false
    }
  }

  // Final comprehensive regex check for overall structure
  // This regex ensures: proper format, valid characters only, proper domain structure
  // Local part: alphanumeric, can contain dots/hyphens/underscores/plus (not at start/end)
  // Domain part: alphanumeric, can contain dots/hyphens (not at start/end), must have valid TLD
  const emailRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9._+-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

/**
 * Generates time slots in 15-minute intervals
 */
export const generateTimeSlots = (lang?: string | null): { value: string; display: string }[] => {
  const slots: { value: string; display: string }[] = []

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push({
        value: formatTimeString(hour, minute),
        display: localizedTime(hour, minute, lang),
      })
    }
  }
  return slots
}

/**
 * Gets user role label based on type and context
 */
export const getUserRoleLabel = (
  userType?: string,
  userId?: string,
  options?: {
    organizerId?: string
    advisorId?: string
    isAdvisor?: boolean
    isExternalGuest?: boolean
  }
): string => {
  // External guests should show nothing
  if (options?.isExternalGuest) {
    return ''
  }

  // Organizer takes priority - show only "Organizer"
  if (options?.organizerId && userId === options.organizerId) {
    return 'Organizer'
  }

  // Check if user is an advisor (BSO Advisor/Admin)
  if (options?.isAdvisor || (options?.advisorId && userId === options.advisorId)) {
    return 'Advisor'
  }

  switch (userType) {
    case 'ADMIN':
      return 'Admin'
    case 'OWNER':
    case 'CO_OWNER':
    case 'MEMBER':
      return ''
    default:
      return ''
  }
}

/**
 * Validates URL format
 * - Requires http/https protocol (case-insensitive)
 * - Domain/subdomain segments must be alphanumeric or hyphen (no consecutive dots)
 * - Must include a valid TLD (2+ letters)
 * - Rejects URLs containing backslashes, control characters, or invalid path characters
 * - Allows URLs in query parameters (e.g., tracking URLs like https://tracker.com/track?url=https://site.com)
 * - Rejects concatenated URLs in the path portion (e.g., https://zoom.us/j/123https://zoom.us/j/456)
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false
  }

  const trimmedUrl = url.trim()

  // Reject URLs containing backslashes (meeting URLs should only use forward slashes)
  if (trimmedUrl.includes('\\')) {
    return false
  }

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(trimmedUrl)) {
    return false
  }

  // Ensure spaces are not present
  if (/\s/.test(trimmedUrl)) {
    return false
  }

  // If the protocol is missing, validate against an https-prefixed copy.
  // Users (e.g. when pasting `meet.google.com/abc-defg-hij`) often omit the scheme.
  const lowerUrl = trimmedUrl.toLowerCase()
  const hasProtocol = lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')
  const candidateUrl = hasProtocol ? trimmedUrl : `https://${trimmedUrl}`

  // Check for multiple URLs concatenated together (e.g., https://zoom.us/j/123https://zoom.us/j/456)
  // Also catches malformed concatenations like https://zoom.us/j/123https:/zoom.us/j/456
  try {
    const urlObj = new URL(candidateUrl)
    const pathOnly = urlObj.pathname
    if (/https?:\/?\/?/i.test(pathOnly)) {
      return false
    }
  } catch {
    // URL parsing failed, will be caught by later validation
  }

  // Check for invalid characters in the path/query portion
  // Reject common problematic characters that shouldn't appear in meeting URLs
  if (/[<>{}|\\^`[\]]/.test(candidateUrl)) {
    return false
  }

  // Regex breakdown:
  // ^https?:\/\/                          -> required protocol (http or https, case-insensitive with 'i' flag)
  // ([a-zA-Z0-9-]+\.)+                    -> one or more domain segments separated by single dots
  // [a-zA-Z]{2,}                          -> TLD (at least two letters)
  // (:\d+)?                               -> optional port
  // (\/[a-zA-Z0-9._~:/?#@!$&'()*+,;=%-]*)?$ -> optional path/query/hash with safe URL characters only
  const urlRegex =
    /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[a-zA-Z0-9._~:/?#@!$&'()*+,;=%-]*)?$/i

  if (!urlRegex.test(candidateUrl)) {
    return false
  }

  // Additional validation: Try using URL constructor as a final sanity check
  try {
    const urlObj = new URL(candidateUrl)
    // Ensure the protocol is http or https
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false
    }
    // Ensure the hostname is not empty
    if (!urlObj.hostname) {
      return false
    }
    return true
  } catch {
    return false
  }
}

/**
 * Prefix `https://` if the URL has no protocol so the saved link is clickable.
 * Returns the input unchanged when empty or already protocol-qualified.
 */
export const normalizeMeetingLink = (url: string): string => {
  const trimmed = (url || '').trim()
  if (!trimmed) return trimmed
  const lower = trimmed.toLowerCase()
  if (lower.startsWith('http://') || lower.startsWith('https://')) return trimmed
  return `https://${trimmed}`
}
